import { useEffect, useRef, useState, useCallback } from 'react';
import { FONTS } from './brandPresets';

const fontsReady = () => Promise.all(FONTS.map((f) => document.fonts.load(`40px "${f}"`).catch(() => null)));

const API = process.env.REACT_APP_BACKEND_URL;
const cache = new Map();

export function loadImg(src) {
  const url = src.startsWith('/api') ? `${API}${src}` : src;
  if (!cache.has(url)) {
    cache.set(url, new Promise((res, rej) => {
      const im = new Image();
      im.crossOrigin = 'anonymous';
      im.onload = () => res(im);
      im.onerror = rej;
      im.src = url;
    }));
  }
  return cache.get(url);
}

function textBox(ctx, L, W) {
  const lines = String(L.text || '').split('\n');
  let size = L.size;
  ctx.font = `${size}px "${L.font}"`;
  let mw = Math.max(1, ...lines.map((t) => ctx.measureText(t).width));
  if (mw > W * 0.96) { size = size * (W * 0.96) / mw; ctx.font = `${size}px "${L.font}"`; mw = W * 0.96; }
  const lh = size * 1.18, bh = lh * lines.length;
  const left = L.align === 'left' ? L.x : L.align === 'right' ? L.x - mw : L.x - mw / 2;
  return { lines, size, lh, x: left, y: L.y - bh / 2, w: mw, h: bh };
}

async function draw(canvas, { w, h, bg, layers, selected }) {
  const ctx = canvas.getContext('2d');
  const boxes = {};
  ctx.clearRect(0, 0, w, h);
  ctx.fillStyle = '#111';
  ctx.fillRect(0, 0, w, h);
  if (bg) {
    const im = await loadImg(bg).catch(() => null);
    if (im) {
      const sc = Math.max(w / im.width, h / im.height);
      ctx.drawImage(im, (w - im.width * sc) / 2, (h - im.height * sc) / 2, im.width * sc, im.height * sc);
    }
  }
  const imgs = await Promise.all(layers.map((L) => (L.type === 'image' ? loadImg(L.src).catch(() => null) : null)));
  layers.forEach((L, i) => {
    if (L.type === 'image') {
      const im = imgs[i];
      if (!im) return;
      const lh = L.w * (im.height / im.width);
      const b = { x: L.x - L.w / 2, y: L.y - lh / 2, w: L.w, h: lh };
      ctx.drawImage(im, b.x, b.y, b.w, b.h);
      boxes[L.id] = b;
    } else {
      const b = textBox(ctx, L, w);
      if (L.backdrop) { ctx.fillStyle = 'rgba(0,0,0,0.55)'; const p = b.size * 0.35; ctx.fillRect(b.x - p, b.y - p, b.w + 2 * p, b.h + 2 * p); }
      ctx.textBaseline = 'middle';
      ctx.textAlign = 'left';
      ctx.lineJoin = 'round';
      b.lines.forEach((t, j) => {
        const lw = ctx.measureText(t).width;
        const tx = L.align === 'center' ? L.x - lw / 2 : b.x;
        const ty = b.y + b.lh * (j + 0.5);
        if (L.stroke) { ctx.lineWidth = Math.max(2, b.size * 0.08); ctx.strokeStyle = 'rgba(0,0,0,0.85)'; ctx.strokeText(t, tx, ty); }
        ctx.fillStyle = L.color;
        ctx.fillText(t, tx, ty);
      });
      boxes[L.id] = b;
    }
  });
  const sb = selected && boxes[selected];
  if (sb) {
    ctx.save();
    ctx.setLineDash([12, 8]);
    ctx.lineWidth = 3;
    ctx.strokeStyle = '#ff7a00';
    ctx.strokeRect(sb.x - 6, sb.y - 6, sb.w + 12, sb.h + 12);
    ctx.restore();
  }
  return boxes;
}

export function BrandCanvas({ size, bg, layers, selected, onSelect, onMove, canvasRef }) {
  const boxes = useRef({});
  const drag = useRef(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let live = true;
    fontsReady().then(() => {
      if (!live || !canvasRef.current) return;
      draw(canvasRef.current, { w: size.w, h: size.h, bg, layers, selected }).then((b) => { boxes.current = b; });
    });
    return () => { live = false; };
  }, [size, bg, layers, selected, tick, canvasRef]);

  useEffect(() => {
    const t = setTimeout(() => setTick((n) => n + 1), 800);
    return () => clearTimeout(t);
  }, []);

  const pt = useCallback((e) => {
    const r = canvasRef.current.getBoundingClientRect();
    return { x: ((e.clientX - r.left) / r.width) * size.w, y: ((e.clientY - r.top) / r.height) * size.h };
  }, [size, canvasRef]);

  const down = (e) => {
    const p = pt(e);
    const hit = [...layers].reverse().find((L) => {
      const b = boxes.current[L.id];
      return b && p.x >= b.x && p.x <= b.x + b.w && p.y >= b.y && p.y <= b.y + b.h;
    });
    onSelect(hit ? hit.id : null);
    if (hit) {
      drag.current = { id: hit.id, dx: p.x - hit.x, dy: p.y - hit.y };
      e.currentTarget.setPointerCapture(e.pointerId);
    }
  };
  const move = (e) => {
    if (!drag.current) return;
    const p = pt(e);
    onMove(drag.current.id, p.x - drag.current.dx, p.y - drag.current.dy);
  };
  const up = () => { drag.current = null; };

  return (
    <canvas ref={canvasRef} width={size.w} height={size.h} data-testid="brand-canvas"
      onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up}
      className="w-full h-auto rounded-xl border border-border bg-black touch-none cursor-move select-none" />
  );
}
